import { INodePoint } from 'app/components/call/SharedSelection/LazySortedNodes';
import { useI18n, tFunctionType } from 'app/components/localization';
import GetParticipantName from 'app/components/rtc/GetParticipantName';
import { KeyPath } from 'app/models/entityStateSync/keypath';
import { ParticipantSelections } from 'app/models/rtc/selection/constants';
import { useCss } from 'app/utils/css';
import { ConsumerValue } from 'app/utils/types';
import { Map } from 'immutable';
import React, { Fragment, FunctionComponent, useEffect, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import { usePresenceContext } from 'app/containers/rtc/PresenceContext';

// Used to prevent screen reader highlight text to be updated too often while user is in the
// middle of highlighting. Text will not be updated until at least this amount of time has
// passed without the inputs changing.
export const DEBOUNCE_INTERVAL = 2000;

interface IProps {
  readonly nodes: Map<KeyPath, INodePoint>;
  readonly selections: ParticipantSelections;
}

interface ISelectedText {
  readonly getUserLabel: (t: tFunctionType) => string;
  readonly text: string;
}

/**
 * Hidden <div> to read out highlight changes to screenreaders.
 */
const SharedSelectionScreenReaderText: FunctionComponent<IProps> = ({
  nodes,
  selections,
}) => {
  const { t } = useI18n();
  const css = useCss();
  const [texts, setTexts] = useState<ISelectedText[]>([]);
  const debounced = useRef<ReturnType<typeof debounce>>();
  const { getParticipantName } = usePresenceContext();

  useEffect(() => {
    debounced.current = debounce(setTextsDebounced, DEBOUNCE_INTERVAL);
    return () => {
      if (debounced.current) {
        debounced.current.cancel();
      }
    };
  }, []);

  useEffect(() => {
    debounced.current && debounced.current(selections, nodes, getParticipantName, setTexts);
  }, [selections, nodes, getParticipantName]);

  return (
    <Fragment>
      {texts.map(({ text, getUserLabel }, i) =>
        <div
          key={`text-${i}`}
          aria-live="polite"
          data-testid={`text-${i}`}
          className={css('screenReader', {
            position: 'absolute',
            left: '-10000px',
            top: 'auto',
            width: '1px',
            height: '1px',
            overflow: 'hidden',
          })}
        >
          {t('lesson.highlighted-screenreader', { user: getUserLabel(t), text })}
        </div>)}
    </Fragment>
  );
};

const setTextsDebounced = (
  selections: ParticipantSelections,
  nodes: Map<KeyPath, INodePoint>,
  getParticipantName: ConsumerValue<typeof GetParticipantName>,
  setTexts: React.Dispatch<React.SetStateAction<ISelectedText[]>>,
) => {
  const texts: ISelectedText[] = [];

  for (const selection of selections.valueSeq()) {
    let result = '';
    const { paths } = selection;

    for (const path of paths) {
      const nodePoint = nodes.get(path);
      if (!nodePoint) continue;

      if (nodePoint.block) {
        result += nodePoint.node.innerText;
      } else {
        const startOffset = path === paths.first() ? selection.startOffset : 0;
        const substringlength = path === paths.last() ? selection.endOffset - startOffset : undefined;
        result += nodePoint.node.innerText.substr(startOffset, substringlength);
      }
      result += ' ';
    }
    if (result.trim().length > 0) {
      texts.push({
        text: result,
        getUserLabel: (tf: tFunctionType) => getParticipantName(selection.user, tf),
      });
    }
  }
  setTexts(texts);
};

export default SharedSelectionScreenReaderText;
